import React, { useEffect, useRef, useState } from "react";
import { Trash2, Upload } from "lucide-react";
import { api, imgUrl } from "@/lib/apiClient";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";

export const AdminBranding = ({ token }) => {
  const [logo, setLogo] = useState(null);
  const [hero, setHero] = useState(null);
  const [busy, setBusy] = useState(false);
  const logoInput = useRef(null);
  const heroInput = useRef(null);
  const authHeader = { headers: { Authorization: `Bearer ${token}` } };

  const load = async () => {
    const res = await api.get("/admin/config", authHeader);
    setLogo(res.data.logo_url);
    setHero(res.data.hero_image_url);
  };

  useEffect(() => {
    load().catch(() => toast.error("Could not load branding."));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const doUpload = async (endpoint, file, onDone) => {
    if (!file) return;
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const res = await api.post(endpoint, fd, authHeader);
      onDone(res.data);
      toast.success("Image updated!");
    } catch {
      toast.error("Upload failed.");
    } finally {
      setBusy(false);
    }
  };

  const removeLogo = async () => {
    setBusy(true);
    try {
      await api.delete("/admin/logo", authHeader);
      setLogo(null);
      toast.success("Logo removed.");
    } catch {
      toast.error("Could not remove logo.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="space-y-8">
      {/* Logo */}
      <section className="rounded-2xl border border-ashy/30 bg-white p-6">
        <h3 className="font-heading text-2xl font-bold text-ink">Business Logo</h3>
        <p className="mt-1 text-sm text-ashydark">Replaces the text logo in the header. Transparent PNG works best.</p>
        <div className="mt-5 flex flex-wrap items-center gap-5">
          <div className="flex h-24 w-40 items-center justify-center rounded-xl border border-dashed border-ashy/50 bg-cream">
            {logo ? <img src={imgUrl(logo)} alt="Logo" className="max-h-20 max-w-36 object-contain" data-testid="admin-logo-preview" /> : <span className="text-xs text-ashydark">No logo yet</span>}
          </div>
          <div className="flex gap-3">
            <input ref={logoInput} type="file" accept="image/*" hidden data-testid="logo-file-input" onChange={(e) => { doUpload("/admin/logo", e.target.files[0], (d) => setLogo(d.logo_url)); e.target.value = ""; }} />
            <Button data-testid="upload-logo-button" disabled={busy} onClick={() => logoInput.current?.click()} className="rounded-full bg-cocoa text-white hover:bg-cocoadark">
              <Upload className="mr-2 h-4 w-4" /> Upload logo
            </Button>
            {logo && <Button data-testid="remove-logo-button" variant="outline" disabled={busy} onClick={removeLogo} className="rounded-full border-ashy/40 text-destructive"><Trash2 className="mr-2 h-4 w-4" /> Remove</Button>}
          </div>
        </div>
      </section>

      {/* Hero image */}
      <section className="rounded-2xl border border-ashy/30 bg-white p-6">
        <h3 className="font-heading text-2xl font-bold text-ink">Homepage Hero Image</h3>
        <p className="mt-1 text-sm text-ashydark">The large photo at the top of your homepage.</p>
        <div className="mt-5 flex flex-wrap items-center gap-5">
          <div className="h-28 w-44 overflow-hidden rounded-xl border border-ashy/40 bg-cream">
            {hero && <img src={imgUrl(hero)} alt="Hero" className="h-full w-full object-cover" data-testid="admin-hero-preview" />}
          </div>
          <input ref={heroInput} type="file" accept="image/*" hidden data-testid="hero-file-input" onChange={(e) => { doUpload("/admin/hero-image", e.target.files[0], (d) => setHero(d.hero_image_url)); e.target.value = ""; }} />
          <Button data-testid="upload-hero-button" disabled={busy} onClick={() => heroInput.current?.click()} className="rounded-full bg-cocoa text-white hover:bg-cocoadark">
            <Upload className="mr-2 h-4 w-4" /> Upload hero image
          </Button>
        </div>
      </section>
    </div>
  );
};
